import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { ProgressSpinner } from "primereact/progressspinner";
import { useState } from "react";
import * as client from "../../../client";
import { useToast } from "../../../context-providers/Toast";
import { SafeReportResponse } from "../Conversion";
import { useEmailThreads } from "../data";
import styles from "./EmailCard.module.css";
import { EmailThreadModal } from "./EmailThreadModal";

const INBOUND_DOMAIN = "inbound.luminorrisk.com";

interface EmailCardProps {
  specialtyPropertyInfo: SafeReportResponse;
}

/** Extract a short subject from the first message of a thread. */
function getThreadSubject(thread: client.EmailThread): string {
  if (thread.messages.length === 0) return "(No subject)";
  return thread.messages[0].subject || "(No subject)";
}

/** Collect unique participant names/emails across all messages in a thread. */
function getThreadParticipants(thread: client.EmailThread): string {
  const participants = new Set<string>();
  for (const msg of thread.messages) {
    if (msg.from) participants.add(msg.from);
  }
  return Array.from(participants).join(", ");
}

/** Get the date of the latest message in a thread. */
function getLatestDate(thread: client.EmailThread): string {
  if (thread.messages.length === 0) return "";
  const last = thread.messages[thread.messages.length - 1];
  const dateStr = last.date ?? "";
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

export const EmailCard: React.FC<EmailCardProps> = ({
  specialtyPropertyInfo,
}) => {
  const reportId = specialtyPropertyInfo.report_id;
  const emailThreads = specialtyPropertyInfo.report_json.email_threads ?? {};
  const hasThreads = Object.keys(emailThreads).length > 0;
  const { data, isLoading, isError } = useEmailThreads(reportId, hasThreads);
  const toast = useToast();

  const [selectedThread, setSelectedThread] =
    useState<client.EmailThread | null>(null);
  const [modalVisible, setModalVisible] = useState(false);

  const threads = data?.threads ?? [];

  const reportEmail = `sub+${reportId}@${INBOUND_DOMAIN}`;

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(reportEmail);
      toast.show({
        severity: "success",
        summary: "Copied",
        detail: "Email address copied to clipboard",
        life: 3000,
      });
    } catch {
      toast.show({
        severity: "error",
        summary: "Copy failed",
        detail: "Could not copy to clipboard",
        life: 3000,
      });
    }
  };

  const handleRowClick = (thread: client.EmailThread) => {
    setSelectedThread(thread);
    setModalVisible(true);
  };

  const copyButton = (
    <div className={styles.buttonContainer}>
      <Button
        label="Copy Email Address (for BCC)"
        icon="pi pi-copy"
        severity="secondary"
        raised
        text
        onClick={handleCopyEmail}
        tooltip={reportEmail}
        tooltipOptions={{ position: "bottom" }}
      />
    </div>
  );

  if (isLoading) {
    return (
      <Card title="Emails" style={{ position: "relative" }}>
        {copyButton}
        <div className={styles.loadingContainer}>
          <ProgressSpinner
            style={{ width: "40px", height: "40px" }}
            strokeWidth="4"
          />
        </div>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card title="Emails" style={{ position: "relative" }}>
        {copyButton}
        <div className={styles.emptyState}>
          <i
            className="pi pi-exclamation-triangle"
            style={{ fontSize: "2rem", color: "var(--red-500)" }}
          />
          <span>Failed to load email threads. Please try again.</span>
        </div>
      </Card>
    );
  }

  if (threads.length === 0) {
    return (
      <Card title="Emails" style={{ position: "relative" }}>
        {copyButton}
        <div className={styles.emptyState}>
          <i className={`pi pi-envelope ${styles.emptyStateIcon}`} />
          <span>No email threads found for this report.</span>
          <span style={{ fontSize: "0.85rem" }}>
            CC or send emails to <strong>{reportEmail}</strong> to track threads
            here.
          </span>
        </div>
      </Card>
    );
  }

  return (
    <Card title="Emails" style={{ position: "relative" }}>
      {copyButton}
      <div className={styles.container}>
        <DataTable
          value={threads}
          selectionMode="single"
          onRowClick={(e) => handleRowClick(e.data as client.EmailThread)}
          rowClassName={() => styles.threadRow}
          rowHover
          stripedRows
        >
          <Column
            header="Subject"
            body={(thread: client.EmailThread) => (
              <span className={styles.subjectColumn}>
                {getThreadSubject(thread)}
              </span>
            )}
            style={{ minWidth: "250px" }}
          />
          <Column
            header="Participants"
            body={(thread: client.EmailThread) => (
              <span className={styles.participantChip}>
                {getThreadParticipants(thread)}
              </span>
            )}
            style={{ minWidth: "200px" }}
          />
          <Column
            header="Date"
            body={(thread: client.EmailThread) => getLatestDate(thread)}
            style={{ width: "120px" }}
          />
          <Column
            header="Messages"
            body={(thread: client.EmailThread) => (
              <span className={styles.messageCount}>
                <i className="pi pi-comments" />
                {thread.messages.length}
              </span>
            )}
            style={{ width: "100px" }}
          />
        </DataTable>
      </div>
      <EmailThreadModal
        visible={modalVisible}
        onHide={() => {
          setModalVisible(false);
          setSelectedThread(null);
        }}
        thread={selectedThread}
      />
    </Card>
  );
};
